import type { EntityAdapter, EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';
import type { WebLegacyWidgetEntity, WebWidgetEntity } from '@web/types';
import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';

import { isLegacyWidget } from '@common/helpers/widget-envelope';

import GridsActions from '@web/store/page-structure/grids/grids.actions';
import WidgetsLegacyActions, { WidgetsActions } from './widgets.actions';
import { type GalleryWidgetData } from '@web/widgets/gallery/type';

export const WIDGETS_FEATURE_KEY = 'widgets';

export type WidgetState = EntityState<WebLegacyWidgetEntity>;

export const widgetsAdapter: EntityAdapter<WebLegacyWidgetEntity> = createEntityAdapter<WebLegacyWidgetEntity>();

/**
 * Re-parent a widget in the slice after a move: the legacy pair (`columnId` for
 * the column, `structureId` for the grid), or a versioned widget's single `parentId`.
 */
function setWidgetParent(widget: WebLegacyWidgetEntity, parentId: string, legacy: () => void): void {
    if (isLegacyWidget(widget)) {
        legacy();
        return;
    }

    (widget as unknown as WebWidgetEntity).parentId = parentId;
}

export const widgetsReducer = createImmerReducer(
    widgetsAdapter.getInitialState(),
    on(WidgetsLegacyActions.init, (state: WidgetState, { widgets }) => widgetsAdapter.setAll(widgets, state)),
    on(WidgetsActions.updateProperties, (state: WidgetState, { update }) => {
        // Widgets share the slice with legacy ones, and it is still typed with the legacy envelope.
        const widget = state.entities[update.id] as unknown as WebWidgetEntity | undefined;
        if (!widget) {
            return state;
        }

        if (update.changes.props) {
            widget.props = { ...widget.props, ...update.changes.props };
        }
        if (update.changes.meta) {
            widget.meta = { ...widget.meta, ...update.changes.meta };
        }

        return state;
    }),
    on(
        WidgetsLegacyActions.widgetCreated,
        WidgetsLegacyActions.addWidget,
        WidgetsLegacyActions.widgetCreatedToGrid,
        WidgetsLegacyActions.addWidgetToGrid,
        (state: WidgetState, { widget }) => widgetsAdapter.upsertOne(widget, state)
    ),
    on(WidgetsActions.created, WidgetsActions.added, (state: WidgetState, { widget }) =>
        widgetsAdapter.upsertOne(widget as unknown as WebLegacyWidgetEntity, state)
    ),
    on(WidgetsActions.copied, (state: WidgetState, { widget }) =>
        widgetsAdapter.addOne(widget as unknown as WebLegacyWidgetEntity, state)
    ),
    on(WidgetsActions.removed, (state: WidgetState, { widget }) => widgetsAdapter.removeOne(widget.id, state)),
    on(WidgetsLegacyActions.widgetMoved, (state: WidgetState, { widgetId, currentColumnId }) => {
        const widget = state.entities[widgetId] as WebLegacyWidgetEntity;
        setWidgetParent(widget, currentColumnId, () => (widget.columnId = currentColumnId));
        return state;
    }),
    on(WidgetsLegacyActions.copyWidget, WidgetsLegacyActions.copyWidgetToGrid, (state: WidgetState, { widget }) =>
        widgetsAdapter.addOne(widget, state)
    ),
    on(
        WidgetsLegacyActions.copyManyWidgets,
        WidgetsLegacyActions.addManyWidgets,
        WidgetsLegacyActions.addManyWidgetsToGrid,
        (state: WidgetState, { widgets }) => widgetsAdapter.addMany(widgets, state)
    ),
    on(WidgetsLegacyActions.removeWidget, WidgetsLegacyActions.removeWidgetFromGrid, (state: WidgetState, { widget }) =>
        widgetsAdapter.removeOne(widget.id, state)
    ),
    on(WidgetsLegacyActions.removeManyWidgets, (state: WidgetState, { widgetsIds }) =>
        widgetsAdapter.removeMany(widgetsIds, state)
    ),
    on(WidgetsLegacyActions.updateManyZeroWidgetsProperties, (state: WidgetState, { updates }) => {
        updates.forEach((update) => {
            const widget = state.entities[update.id];
            if (widget) {
                widget.data = { ...widget.data, ...update.changes.data };

                if (widget.styles || update.changes.styles) {
                    widget.styles = { ...widget.styles, ...update.changes.styles };
                }

                if (widget.mobileStyles || update.changes.mobileStyles) {
                    widget.mobileStyles = { ...widget.mobileStyles, ...update.changes.mobileStyles };
                }

                if (update.changes.displayId) {
                    widget.displayId = update.changes.displayId;
                }
            }
        });
        return state;
    }),
    on(
        WidgetsLegacyActions.updateWidgetProperties,
        WidgetsLegacyActions.syncWidgetProperties,
        WidgetsLegacyActions.updateZeroWidgetProperties,
        (state: WidgetState, { update }) => {
            const widget = state.entities[update.id];
            if (!widget) {
                return state;
            }

            widget.data = { ...widget.data, ...update.changes.data };

            if (widget.styles || update.changes.styles) {
                widget.styles = { ...widget.styles, ...update.changes.styles };
            }
            // widget.styles = { ...widget.styles, ...update.changes.styles };

            if (widget.mobileStyles || update.changes.mobileStyles) {
                widget.mobileStyles = { ...widget.mobileStyles, ...update.changes.mobileStyles };
            }
            // widget.mobileStyles = { ...widget.mobileStyles, ...update.changes.mobileStyles };

            // Presence, not truthiness: an empty id resets the element id to its `widget-<id>` default.
            if ('displayId' in update.changes) {
                widget.displayId = update.changes.displayId;
            }

            return state;
        }
    ),
    on(WidgetsLegacyActions.updateWidgetDisplayId, (state: WidgetState, { id, displayId }) => {
        const widget = state.entities[id];
        if (!widget) {
            return state;
        }

        widget.displayId = displayId;
        return state;
    }),
    on(WidgetsLegacyActions.updateWidgetData, (state: WidgetState, { update }) => {
        const widget = state.entities[update.id];
        if (!widget) {
            return state;
        }

        widget.data = { ...widget.data, ...update.changes.data };
        return state;
    }),
    on(WidgetsLegacyActions.updateGalleryImageData, (state: WidgetState, { id, image, index }) => {
        const widget = state.entities[id];
        if (!widget) {
            return state;
        }

        const data = widget.data as GalleryWidgetData;
        data.images[index] = { ...data.images[index], ...image };

        return state;
    }),
    on(WidgetsLegacyActions.updateWidgetStyles, (state: WidgetState, { update }) => {
        const widget = state.entities[update.id];
        if (!widget) {
            return state;
        }

        widget.styles = { ...widget.styles, ...update.changes.styles };
        return state;
    }),
    on(GridsActions.moveWidgetFromGridToColumn, (state: WidgetState, { widgetId, nextParentId }) => {
        const widget = state.entities[widgetId];
        if (!widget) {
            return state;
        }

        setWidgetParent(widget, nextParentId, () => delete widget.structureId);
        return state;
    }),
    on(
        GridsActions.moveWidgetFromGridToGrid,
        GridsActions.moveWidgetFromColumnToGrid,
        (state: WidgetState, { widgetId, nextParentId }) => {
            const widget = state.entities[widgetId];
            if (!widget) {
                return state;
            }

            setWidgetParent(widget, nextParentId, () => (widget.structureId = nextParentId));
            return state;
        }
    )
);
